<script>
import { useSavingsCalculations } from '../composables/useSavingsCalculations'
import { useBudgetData } from '../composables/useBudgetData'
import { useSavingsGoals } from '../composables/useSavingsGoals'
import ParticlePile from './ParticlePile.vue';

export default {
  name: "RunningSavings",
  components: {
    ParticlePile
  },
  setup() {
    const { currentDate, daysInMonth, dailyBudget } = useBudgetData()
    const { totalSavedThisMonth, averageDailySaved, projectedEndMonthSavings } = useSavingsCalculations()
    const { activeGoalsProgressPercentage } = useSavingsGoals()

    return {
      currentDate,
      daysInMonth,
      dailyBudget,
      totalSavedThisMonth,
      averageDailySaved,
      projectedEndMonthSavings,
      activeGoalsProgressPercentage
    }
  },
  computed: {
    savedThisMonth() {
      return this.totalSavedThisMonth;
    },
    averageSaved() {
      return this.averageDailySaved;
    },
    currentDateNumber() {
      return this.currentDate.getDate();
    },
    monthProgress() {
      return Math.round((this.currentDateNumber / this.daysInMonth) * 100);
    },
    projectedMonthlySavings() {
      return this.projectedEndMonthSavings;
    },
    savingsRate() {
      if (this.dailyBudget === 0) return 0;
      return Math.round((this.averageSaved / this.dailyBudget) * 100);
    }
  }
}
</script>

<template>
  <div class="running-savings-card card savings-positive">

    <!-- Card Header -->
    <div class="card-header">
      <div class="header-icon">
        <i class="bi bi-calendar-check"></i>
      </div>
      <div class="header-content">
        <h3 class="card-title">Saved This Month</h3>
        <p class="card-subtitle">Your progress so far</p>
      </div>
    </div>

    <!-- Card Body -->
    <div class="card-body">
      <div class="row mb-2">
        <div class="col">
          <label for="transactionDescription" class="form-label fw-semibold">
            <i class="fa-solid fa-dollar-sign me-1"></i>Saved This Month
          </label>
        </div>
      </div>
      <div class="row">
        <div class="col text-center">
          <span class="amount-value">{{ savedThisMonth.toFixed(2) }}</span>
        </div>
      </div>
      <div class="row mt-3">
        <div class="col">
          <label for="transactionDescription" class="form-label fw-semibold">
            <i class="fa-solid fa-up-right-and-down-left-from-center me-1"></i>Projected Month-End
          </label>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <span class="stat-value income-value">${{ projectedMonthlySavings.toFixed(2) }}</span>
        </div>
      </div>
      <div class="row mb-1 mt-4">
        <div class="col">
          <label for="transactionDescription" class="form-label fw-semibold">
            <i class="fa-solid fa-box"></i> Savings Pile Up
          </label>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <ParticlePile :percent-complete="activeGoalsProgressPercentage" :auto-start="true" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.running-savings-card:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
}

.savings-negative .header-icon {
  background: linear-gradient(135deg, var(--light-danger), #ff6b6b);
}

.header-content {
  flex: 1;
  text-align: left;
}

/* Progress Section */
.progress-section {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 12px;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.progress-label {
  font-size: 0.8rem;
  color: var(--light-text-muted);
  font-weight: 500;
}

.progress-days {
  font-size: 0.8rem;
  color: var(--light-text);
  font-weight: 600;
}

.progress-bar-container {
  width: 100%;
  height: 8px;
  background: var(--light-border);
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.progress-bar {
  height: 100%;
  border-radius: 4px;
  transition: all 0.6s ease;
  background: var(--light-primary);
}

.progress-percentage {
  font-size: 0.75rem;
  color: var(--light-text-muted);
  font-weight: 500;
}

/* Statistics Section */
.stats-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 12px;
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  color: var(--light-text);
}

.expense-value {
  color: var(--light-danger);
}

.income-value {
  color: var(--light-success);
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
}

.stat-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  flex-shrink: 0;
}

.daily-icon {
  background: rgba(13, 110, 253, 0.1);
  color: #0d6efd;
}

.projection-icon {
  background: rgba(40, 167, 69, 0.1);
  color: var(--light-success);
}

.stat-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
}

.stat-label {
  font-size: 0.75rem;
  color: var(--light-text-muted);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1;
}

.daily-value {
  color: #0d6efd;
}

.projection-value {
  color: var(--light-success);
}

.stat-divider {
  width: 1px;
  height: 40px;
  background: var(--light-border);
  margin: 0 1rem;
  flex-shrink: 0;
}

/* Savings Rate Section */
.rate-section {
  padding: 1rem;
}

.rate-indicator {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 12px;
}

.rate-excellent {
  background: rgba(40, 167, 69, 0.1);
  border-color: var(--light-success);
}

.rate-good {
  background: rgba(13, 110, 253, 0.1);
  border-color: #0d6efd;
}

.rate-fair {
  background: rgba(255, 193, 7, 0.1);
  border-color: #ffc107;
}

.rate-poor {
  background: rgba(255, 108, 55, 0.1);
  border-color: #ff6c37;
}

.rate-negative {
  background: rgba(220, 53, 69, 0.1);
  border-color: var(--light-danger);
}

.rate-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
  border: 1px solid gray
}

.rate-excellent .rate-icon {
  background: var(--light-success);
  color: white;
}

.rate-good .rate-icon {
  background: #0d6efd;
  color: white;
}

.rate-fair .rate-icon {
  background: #ffc107;
  color: white;
}

.rate-poor .rate-icon {
  background: #ff6c37;
  color: white;
}

.rate-negative .rate-icon {
  background: var(--light-danger);
  color: white;
}

.rate-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
}

.rate-label {
  font-size: 0.75rem;
  color: var(--light-text-muted);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
}

.rate-value {
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--light-text);
  margin-bottom: 0.25rem;
}

.rate-description {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--light-text-muted);
}

/* Dark Mode Adjustments */
:global([data-theme]) .progress-section {
  background: rgba(255, 255, 255, 0.05);
}

:global([data-theme]) .stats-section {
  background: rgba(255, 255, 255, 0.05);
}

:global([data-theme]) .progress-bar-container {
  background: var(--light-border);
}

/* Responsive Design */
@media (max-width: 768px) {
  .stats-section {
    flex-direction: column;
    gap: 1rem;
    padding: 0.75rem;
  }

  .stat-item {
    width: 100%;
    justify-content: center;
  }

  .stat-divider {
    width: 100%;
    height: 1px;
    margin: 0;
  }

  .stat-content {
    align-items: center;
    text-align: center;
  }

  .rate-indicator {
    flex-direction: column;
    text-align: center;
    gap: 0.75rem;
  }

  .rate-content {
    align-items: center;
  }
}
</style>